import { useCallback, useContext } from 'react';

import { ScrollRegionContext } from '../components/layout/ScrollRegion/ScrollRegionContext';

export type UseScrollToOptions = {
  /** Only used to say which caller is at fault when the options below do not add up. */
  name?: string;
  /**
   * The box whose ends `to` means. **Any scrolling element will do** - a `ScrollRegion` is simply
   * the one that can be found without being handed over, so inside one this can be left out.
   */
  scrollerRef?: React.RefObject<HTMLElement | null>;
  /**
   * Where to go, and **exactly one of the three.** An end of `scrollerRef`, or of the surrounding
   * `ScrollRegion` when there is one and no `scrollerRef`.
   */
  to?: 'top' | 'bottom';
  /** Where to go: this element, brought into view. */
  toObjectRef?: React.RefObject<HTMLElement | null>;
  /**
   * Where to go, named instead of held: a `data-test` name, or `#` and a DOM id.
   *
   * **The first match wins, and a name can be worn by more than one element** - so this is the
   * option for a caller that cannot hold a ref, not the one to reach for first. The search is
   * scoped to `scrollerRef` or the surrounding `ScrollRegion` where there is either, which is
   * usually enough to make a shared name unambiguous.
   */
  toTarget?: string;
};

/**
 * Somewhere worth being, and how to get there - without a control of its own, so it can be hung off
 * anything that can be clicked.
 *
 * `destination` comes back as well as the scroll, because the end being gone to is usually also
 * what a caller wants to point an arrow at or decide its own visibility by.
 */
export const useScrollTo = ({
  name = 'useScrollTo',
  scrollerRef,
  to,
  toObjectRef,
  toTarget,
}: UseScrollToOptions = {}) => {
  const region = useContext(ScrollRegionContext);

  const locators = [
    to && 'to',
    toObjectRef && 'toObjectRef',
    toTarget && 'toTarget',
  ].filter(Boolean);
  const destination = locators.length === 0 ? 'bottom' : to;

  const scrollTo = useCallback(() => {
    /*
      **Whatever was clicked is blurred first, and that is the whole of this working.** A control
      holds focus after a click, and the browser scrolls a newly focused element into view - which
      abandons a smooth scroll just started. Measured under a real mouse click: 0 of 976 arriving
      with focus held, 976 of 976 without.
    */
    (document.activeElement as HTMLElement | null)?.blur();

    /*
      **Read here, not during render.** A ref is still empty on the first render, and a caller that
      never renders again would hold a box resolved as `null` for its whole life.
    */
    const scroller = scrollerRef?.current ?? region?.regionRef.current ?? null;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)')
      .matches
      ? 'auto'
      : 'smooth';

    if (destination) {
      scroller?.scrollTo({
        behavior,
        top: destination === 'top' ? 0 : scroller.scrollHeight,
      });

      return;
    }

    const element =
      toObjectRef?.current ??
      (scroller ?? document).querySelector<HTMLElement>(
        toTarget?.startsWith('#') ? toTarget : `[data-test="${toTarget}"]`
      );

    element?.scrollIntoView({ behavior, block: 'start' });
  }, [destination, region, scrollerRef, toObjectRef, toTarget]);

  /*
    **Thrown rather than resolved by precedence.** Two of these mean the caller believes the scroll
    goes somewhere it does not, and picking a winner would send the reader there quietly.
  */
  if (locators.length > 1) {
    throw new Error(
      `${name} was given ${locators.join(' and ')}. Say where to go exactly once.`
    );
  }

  if (destination && !scrollerRef && !region) {
    throw new Error(
      `${name} was asked for the ${destination} of a box it was not given. Pass scrollerRef, or call it inside a ScrollRegion.`
    );
  }

  return { destination, scrollTo };
};
